import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { preparePagination } from './utils';
import { TDispatch } from '../../types/redux';
import { IPaginationItem } from '../../types/layouts/layoutsCatalog';
import { selectPagination } from '../../selectors/layoutsCatalog';
import { changePage } from '../../thunks';

interface IPagination {
  pages: IPaginationItem[];
  onPageSelect(page: number): void;
  nextPage?: IPaginationItem;
  prevPage?: IPaginationItem;
}

export function usePagination(): IPagination {
  const dispatch = useDispatch<TDispatch>();

  const pagination = useSelector(selectPagination);

  const totalPages = React.useMemo(() => pagination?.total || 0, [pagination]);
  const page = React.useMemo(() => pagination?.currentPage || 1, [pagination]);

  const onPageSelect = React.useCallback((page: number) => dispatch(changePage(page)), [dispatch]);

  const pages = React.useMemo(
    () => (totalPages <= 1 ? [] : preparePagination({ currentPage: page, totalPages })),
    [page, totalPages],
  );

  const prevPage = React.useMemo<IPaginationItem>(
    () => ({
      disabled: page === 1,
      value: page - 1,
      label: 'Назад',
    }),
    [page],
  );

  const nextPage = React.useMemo<IPaginationItem>(
    () => ({
      disabled: page === totalPages,
      value: page + 1,
      label: 'Дальше',
    }),
    [page, totalPages],
  );

  return React.useMemo(
    () => ({
      nextPage,
      prevPage,
      pages,
      onPageSelect,
    }),
    [nextPage, onPageSelect, pages, prevPage],
  );
}
